import * as Sentry from "@sentry/react";

/**
 * Check if Sentry has been initialized
 * @returns true if Sentry client is available, false otherwise
 */
export function isSentryInitialized(): boolean {
  try {
    const client = Sentry.getClient();
    return client !== undefined;
  } catch {
    return false;
  }
}

/**
 * Safely capture an exception to Sentry
 * Only captures if Sentry is initialized
 * @param error - The error to capture
 * @param context - Optional additional context
 */
export function safeCaptureException(
  error: Error,
  context?: {
    contexts?: Record<string, unknown>;
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
): void {
  if (!isSentryInitialized()) {
    if (import.meta.env.DEV) {
      console.warn("Sentry not initialized, error not captured:", error);
    }
    return;
  }

  Sentry.withScope((scope) => {
    if (context?.contexts) {
      Object.entries(context.contexts).forEach(([key, value]) => {
        if (value !== null && typeof value === "object") {
          scope.setContext(key, value as Record<string, unknown>);
        } else if (value === null) {
          scope.setContext(key, null);
        }
      });
    }
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureException(error);
  });
}

/**
 * Safely set user context in Sentry
 * Only sets user if Sentry is initialized
 * @param user - User information to set
 */
export function safeSetUser(user: {
  id?: string;
  email?: string;
  username?: string;
  name?: string;
  [key: string]: unknown;
}): void {
  if (!isSentryInitialized()) {
    if (import.meta.env.DEV) {
      console.warn("Sentry not initialized, user context not set");
    }
    return;
  }

  Sentry.setUser(user);
}

/**
 * Safely add breadcrumb to Sentry
 * Only adds breadcrumb if Sentry is initialized
 * @param breadcrumb - Breadcrumb information
 */
export function safeAddBreadcrumb(
  breadcrumb: Sentry.Breadcrumb
): void {
  if (!isSentryInitialized()) {
    return;
  }

  Sentry.addBreadcrumb(breadcrumb);
}

